import { get, orderBy, set } from "lodash-es";
import type { Promisable } from "type-fest";
import * as _ from "../routes/_index";
import * as _travel from "../routes/travel";
import * as _travel_$countrySlug from "../routes/travel_.$country";
import * as _travel_$countrySlug_$townSlug from "../routes/travel_.$country_.$town";
import * as _travel_random from "../routes/travel_.random";
import type { ImageVariants } from "./imageUtils";

export type SitemapDataFunction = () => Promisable<Page | Page[]>;

export interface Page {
	pathname: string;
	title: string;
	sitemapTitle?: string;
	description?: string;
	date?: string;
	images?: PageImage[];
	order?: number;
	isExternalToRemix?: boolean;
}

export interface PageImage {
	src: ImageVariants;
	caption: string;
	geoLocation?: string;
}

export type SitemapCategory = {
	[pathname: string]: Page & {
		children: SitemapCategory;
	};
};

export async function getSitemapPages(): Promise<Page[]> {
	const pages = await Promise.all([
		_.sitemap(),
		_travel.sitemap(),
		_travel_$countrySlug.sitemap(),
		_travel_$countrySlug_$townSlug.sitemap(),
		_travel_random.sitemap(),
		{
			pathname: "/storybook",
			title: "Website component Library",
			description: "Storybook component library for this website.",
			isExternalToRemix: true,
		},
	]);

	return pages.flat();
}

export function createPageHierarchy(pages: Page[]) {
	// Order the pages by path so that a simple loop through them will alway
	// process parent pages before the children. e.g. "/foo" before "/foo/bar".
	const orderedPages = orderBy(pages, (page) => page.pathname);
	const root: SitemapCategory = {};

	for (const page of orderedPages) {
		const pathnameParts = page.pathname
			.split("/")
			.filter((value) => value !== "")
			.flatMap((pathPortion) => [pathPortion, "children"])
			.slice(0, -1);

		if (pathnameParts.length === 0) {
			// Add homepage to root
			pathnameParts.push("/");
		}

		if (pathnameParts.length > 1) {
			// `page.pathname` is not a root page in sitemap (e.g. "/", "/foo",
			// "/bar") - this is not the root page. Check the parent exists. It should
			// be here already since we ordered the pages by pathname earlier on.

			// TODO: This looks complex. Simplify it
			if (!get(root, pathnameParts.slice(0, -1))) {
				throw new Error(
					`No parent page found in sitemap for page "${page.pathname}".`,
				);
			}
		}

		const pageNewFormat: SitemapCategory[string] = {
			...page,
			children: {},
		};

		set(root, pathnameParts, pageNewFormat);
	}

	// TODO: Sort keys by `order` property
	return root;
}
